import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ProposalConversation } from '../models/ProposalConversation';
import { format } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';
import { zoullooColors } from '../theme';

interface ProposalConversationThreadProps {
  conversations: ProposalConversation[];
  isLoading?: boolean;
  error?: Error | null;
}

const ProposalConversationThread: React.FC<ProposalConversationThreadProps> = ({
  conversations,
  isLoading = false,
  error = null
}) => {
  const { t, i18n } = useTranslation();

  const getUserTypeColor = (userType: string): string => {
    switch (userType.toLowerCase()) {
      case 'freelancer':
        return zoullooColors.primary.main; // Coquelicot #FF6300
      case 'client':
        return zoullooColors.secondary.main; // Persian Blue #4836bf
      case 'internal':
        return zoullooColors.success.main; // Vert #4CAF50
      default:
        return zoullooColors.grey[450];
    }
  };

  const getUserTypeLabel = (userType: string) => {
    return t(`proposal.conversation.userType.${userType.toLowerCase()}`);
  };

  const getMessageTypeLabel = (messageType: string) => {
    return t(`proposal.conversation.messageType.${messageType.toLowerCase()}`);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const locale = i18n.language === 'fr' ? fr : enUS;
    return format(date, 'PPp', { locale });
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        {t('proposal.conversation.loadError')}
      </Alert>
    );
  }

  if (conversations.length === 0) {
    return null;
  }

  const sortedConversations = [...conversations].reverse();

  return (
    <Box sx={{ py: 2 }}>
      <Typography variant="h6" sx={{ mb: 3 }}>
        {t('proposal.conversation.title')}
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {sortedConversations.map((conversation) => {
          return (
            <Box key={conversation.id}>
              <Paper
                elevation={1}
                sx={{
                  p: 2,
                  backgroundColor: 'background.paper',
                  borderLeft: 4,
                  borderLeftColor: getUserTypeColor(conversation.senderUserType)
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Chip
                    label={getUserTypeLabel(conversation.senderUserType)}
                    size="small"
                    variant="outlined"
                    sx={{
                      borderColor: getUserTypeColor(conversation.senderUserType),
                      color: zoullooColors.text.primary,
                      fontWeight: 500
                    }}
                  />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {conversation.senderUserName}
                  </Typography>
                  {conversation.messageType !== 'General' && (
                    <Chip
                      label={getMessageTypeLabel(conversation.messageType)}
                      size="small"
                      variant="outlined"
                      sx={{
                        color: zoullooColors.text.primary,
                        fontWeight: 500
                      }}
                    />
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {formatDate(conversation.createdAt)}
                </Typography>
              </Box>

              {conversation.messageType === 'CastingSlots' ? (
                <Box
                  sx={{
                    mt: 1,
                    p: 2,
                    backgroundColor: 'info.lighter',
                    borderRadius: 1,
                    border: 1,
                    borderColor: 'info.light'
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'info.dark' }}>
                    {t('proposal.conversation.castingSlots')}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word'
                    }}
                  >
                    {conversation.message}
                  </Typography>
                </Box>
              ) : (
                <Typography
                  variant="body1"
                  sx={{
                    mt: 1,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word'
                  }}
                >
                  {conversation.message}
                </Typography>
              )}
              </Paper>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default ProposalConversationThread;
